import React, { useState } from 'react';
import './AccountDeletion.css';

const API_ENDPOINT = 'https://backend.optimiz.dhanveen.com/user/v2/account-deletion-request';

const AccountDeletionForm = () => {
    const [formData, setFormData] = useState({
        phoneNumber: '',
        fullName: '',
        reason: '',
        confirmation: false
    });
    const [status, setStatus] = useState('');
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
        setErrors({ ...errors, [name]: '' });
    };

    const validateForm = () => {
        let formErrors = {};
        if (!formData.phoneNumber.match(/^[0-9]{10}$/)) {
            formErrors.phoneNumber = "Please enter a valid 10-digit phone number.";
        }
        if (!formData.fullName.trim()) {
            formErrors.fullName = "Full name is required for verification.";
        }
        if (!formData.confirmation) {
            formErrors.confirmation = "You must confirm account deletion.";
        }
        return formErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formErrors = validateForm();
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }
        setStatus('sending');
        try {
            const payload = {
                phoneNumber: formData.phoneNumber,
                fullName: formData.fullName,
                reason: formData.reason,
                confirmation: formData.confirmation ? "true" : "false",
                timestamp: new Date().toISOString()
            };
            const response = await fetch(API_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (response.ok) {
                setStatus('success');
                setFormData({
                    phoneNumber: '',
                    fullName: '',
                    reason: '',
                    confirmation: false
                });
                setErrors({});
            } else {
                setStatus('error');
            }
        } catch (error) {
            setStatus('error');
        }
    };

    return (
        <div className="account-deletion-page">
            <div className="account-deletion-container">
                {/* Header */}
                <div className="account-deletion-header">
                    <h1>Account Deletion Request</h1>
                    <p>Permanently delete your account and all associated data</p>
                </div>

                {/* Main Content */}
                <div className="account-deletion-content">
                    {/* Information Section */}
                    <div className="account-deletion-info">
                        <div className="info-card">
                            <h2>⚠️ Important Information</h2>
                            <div className="warning-content">
                                <h3>What happens when you delete your account:</h3>
                                <ul>
                                    <li>All your personal data will be permanently deleted</li>
                                    <li>Your account cannot be recovered after deletion</li>
                                    <li>All app data will be lost</li>
                                </ul>
                            </div>
                        </div>

                        <div className="info-card">
                            <h2>📋 Process Timeline</h2>
                            <div className="process-steps">
                                <div className="step">
                                    <span className="step-number">1</span>
                                    <div>
                                        <strong>Request Submission</strong>
                                        <p>Fill out the form with your details</p>
                                    </div>
                                </div>
                                <div className="step">
                                    <span className="step-number">2</span>
                                    <div>
                                        <strong>Verification Call</strong>
                                        <p>We'll contact you within 24-48 hours</p>
                                    </div>
                                </div>
                                <div className="step">
                                    <span className="step-number">3</span>
                                    <div>
                                        <strong>Account Deletion</strong>
                                        <p>Completed within 6-7 business days</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="info-card contact-info">
                            <h2>📞 Need Help?</h2>
                            <p className='text-dark'>If you have questions about account deletion or need assistance, please contact our support team before proceeding.</p>
                            <div className="contact-details">
                                <p className='text-dark'><strong>Email:</strong> <a href="mailto:info@dhanveen.com" className='text-dark text-accent'>info@dhanveen.com</a></p>
                                <p className='text-dark'><strong>Phone:</strong> +91 9737949264</p>
                            </div>
                        </div>
                    </div>

                    {/* Form Section */}
                    <div className="account-deletion-form-section">
                        <div className="form-card">
                            <h2>Delete My Account</h2>
                            <p className="form-subtitle">Please provide the following information to proceed with account deletion</p>

                            <form onSubmit={handleSubmit} className="deletion-form">
                                <div className="form-group">
                                    <label htmlFor="phoneNumber" className="form-label">
                                        Registered Phone Number *
                                    </label>
                                    <input
                                        type="tel"
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        placeholder="Enter your 10-digit phone number"
                                        value={formData.phoneNumber}
                                        onChange={handleChange}
                                        className={`form-input ${errors.phoneNumber ? 'error' : ''}`}
                                        maxLength={10}
                                        required
                                    />
                                    {errors.phoneNumber && <p className="error-text">{errors.phoneNumber}</p>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="fullName" className="form-label">
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="fullName"
                                        name="fullName"
                                        placeholder="Enter your full name as registered"
                                        value={formData.fullName}
                                        onChange={handleChange}
                                        className={`form-input ${errors.fullName ? 'error' : ''}`}
                                        required
                                    />
                                    {errors.fullName && <p className="error-text">{errors.fullName}</p>}
                                </div>

                                <div className="form-group">
                                    <label htmlFor="reason" className="form-label">
                                        Reason for Deletion (Optional)
                                    </label>
                                    <select
                                        id="reason"
                                        name="reason"
                                        value={formData.reason}
                                        onChange={handleChange}
                                        className="form-input"
                                    >
                                        <option value="">Select a reason (optional)</option>
                                        <option value="privacy-concerns">Privacy concerns</option>
                                        <option value="no-longer-needed">No longer need the service</option>
                                        <option value="switching-services">Switching to another service</option>
                                        <option value="technical-issues">Technical issues</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                {formData.reason === 'other' && (
                                    <div className="form-group">
                                        <textarea
                                            name="reason"
                                            placeholder="Please specify your reason..."
                                            value={formData.reason}
                                            onChange={handleChange}
                                            className="form-textarea"
                                            rows={3}
                                        ></textarea>
                                    </div>
                                )}

                                <div className="form-group checkbox-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="confirmation"
                                            checked={formData.confirmation}
                                            onChange={handleChange}
                                            className="checkbox-input"
                                            required
                                        />
                                        <span className="checkbox-text">
                                            I understand that this action is irreversible. I confirm that I want to permanently delete my account and all associated data. I understand that I will be contacted at my registered phone number for final verification before the deletion is processed.
                                        </span>
                                    </label>
                                    {errors.confirmation && <p className="error-text">{errors.confirmation}</p>}
                                </div>

                                <button
                                    type="submit"
                                    disabled={status === 'sending'}
                                    className="submit-button"
                                >
                                    {status === 'sending' ? 'Processing Request...' : 'Submit Deletion Request'}
                                </button>

                                {status === 'success' && (
                                    <div className="status-message success">
                                        <h3>✅ Request Submitted Successfully</h3>
                                        <p>
                                            Thank you for submitting your account deletion request. Here's what happens next:
                                        </p>
                                        <ul>
                                            <li>We will contact you at <strong>{formData.phoneNumber}</strong> within 24-48 hours</li>
                                            <li>Our team will verify your identity over the phone</li>
                                            <li>Once verified, your account will be deleted within 6-7 business days</li>
                                            <li>You will receive a confirmation email once the deletion is complete</li>
                                        </ul>
                                    </div>
                                )}

                                {status === 'error' && (
                                    <div className="status-message error">
                                        <h3>❌ Request Failed</h3>
                                        <p>We couldn't process your deletion request at the moment. Please try again or contact our support team for assistance.</p>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AccountDeletionForm;
